import React, { useEffect, useState, useCallback, useRef } from 'react'
import Container from '../../Components/Container'
import { FaEye, FaEyeSlash } from 'react-icons/fa'
import { FiUser, FiLock, FiSave, FiX } from 'react-icons/fi'
import toast, { Toaster } from 'react-hot-toast'
import { motion, AnimatePresence } from 'framer-motion'

const readJson = async (res) => {
    if (!res.ok) throw new Error('Sunucu yanıt vermedi')
    return res.json()
}

const isSelectError = (data) =>
    data != null &&
    typeof data === 'object' &&
    !Array.isArray(data) &&
    data.status === 400

/** Mevcut profile.php güncelleme yanıtı ile uyum (400 = hata) */
const isUpdateSuccess = (data) =>
    data != null &&
    typeof data === 'object' &&
    !Array.isArray(data) &&
    Number(data.status) !== 400

const parseProfile = (payload) => {
    if (payload == null) return { name: '' }
    if (Array.isArray(payload)) {
        return {
            name: String(payload[0] ?? ''),
        }
    }
    if (typeof payload === 'object') {
        return {
            name: String(
                payload.name ??
                    payload.user ??
                    payload.username ??
                    ''
            ),
        }
    }
    return { name: '' }
}

const Profile = () => {
    const [open, setOpen] = useState(false)
    const [name, setName] = useState('')
    const [currentPassword, setCurrentPassword] = useState('')
    const [newPassword, setNewPassword] = useState('')
    const [newPasswordAgain, setNewPasswordAgain] = useState('')
    const [showCurrentPassword, setShowCurrentPassword] = useState(false)
    const [loadingProfile, setLoadingProfile] = useState(true)
    const [submitting, setSubmitting] = useState(false)

    const loadProfile = useCallback(async () => {
        const formdata = new FormData()
        formdata.append('action', 'select')
        const payload = await fetch(
            process.env.REACT_APP_BASE_URL + 'profile.php',
            { method: 'POST', body: formdata }
        ).then(readJson)

        if (isSelectError(payload)) {
            toast.error(payload.message || 'Profil alınamadı')
            setName('')
            return
        }
        const { name: n } = parseProfile(payload)
        setName(n)
    }, [])

    useEffect(() => {
        let cancelled = false
        setLoadingProfile(true)
        ;(async () => {
            try {
                await loadProfile()
            } catch {
                if (!cancelled) {
                    toast.error('Profil yüklenirken hata oluştu')
                    setName('')
                }
            } finally {
                if (!cancelled) setLoadingProfile(false)
            }
        })()
        return () => {
            cancelled = true
        }
    }, [loadProfile])

    const closeModal = useCallback(() => {
        if (submitting) return
        setOpen(false)
        setNewPassword('')
        setNewPasswordAgain('')
    }, [submitting])

    const closeModalRef = useRef(closeModal)
    closeModalRef.current = closeModal

    useEffect(() => {
        if (!open) return
        const onKey = (e) => {
            if (e.key === 'Escape' && !submitting)
                closeModalRef.current()
        }
        document.addEventListener('keydown', onKey)
        const prev = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => {
            document.removeEventListener('keydown', onKey)
            document.body.style.overflow = prev
        }
    }, [open, submitting])

    const submitPasswordChange = async () => {
        if (!String(currentPassword).trim()) {
            toast.error('Önce ana ekranda mevcut şifrenizi girin')
            return
        }
        if (!String(newPassword).trim()) {
            toast.error('Yeni şifre girin')
            return
        }
        if (newPassword.length < 4) {
            toast.error('Yeni şifre en az 4 karakter olmalı')
            return
        }
        if (newPassword !== newPasswordAgain) {
            toast.error('Yeni şifreler eşleşmiyor')
            return
        }

        setSubmitting(true)
        try {
            const formdata = new FormData()
            formdata.append('action', 'update')
            /** Sunucu alan adları (eski istemci ile aynı): mevcut → password, yeni çifti → oldPassword / oldPasswordAgain */
            formdata.append('password', currentPassword)
            formdata.append('oldPassword', newPassword)
            formdata.append('oldPasswordAgain', newPasswordAgain)

            const data = await fetch(
                process.env.REACT_APP_BASE_URL + 'profile.php',
                { method: 'POST', body: formdata }
            ).then(readJson)

            if (!isUpdateSuccess(data)) {
                toast.error(data.message || 'Şifre güncellenemedi')
                return
            }
            toast.success(data.message || 'Şifre güncellendi')
            setOpen(false)
            setNewPassword('')
            setNewPasswordAgain('')
            setCurrentPassword('')
            await loadProfile()
        } catch {
            toast.error('İstek başarısız oldu')
        } finally {
            setSubmitting(false)
        }
    }

    const inputClass =
        'w-full rounded-xl border border-slate-200 bg-slate-50/80 px-3.5 py-2.5 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 disabled:opacity-60'

    return (
        <Container padding bg='#f8fafc'>
            <Toaster
                position='top-right'
                toastOptions={{ className: 'text-sm font-medium shadow-lg' }}
            />

            <AnimatePresence>
                {open && (
                    <motion.div
                        key='profile-pw-modal'
                        role='presentation'
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className='fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 p-4 backdrop-blur-sm sm:items-center'
                        onClick={() => !submitting && closeModal()}
                    >
                        <motion.div
                            role='dialog'
                            aria-modal='true'
                            aria-labelledby='profile-pw-title'
                            initial={{ scale: 0.96, opacity: 0, y: 12 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.96, opacity: 0, y: 12 }}
                            transition={{
                                type: 'spring',
                                damping: 26,
                                stiffness: 320,
                            }}
                            className='w-full max-w-md overflow-hidden rounded-t-2xl border border-slate-200 bg-white shadow-2xl sm:rounded-2xl'
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className='border-b border-slate-100 px-6 py-5'>
                                <div className='flex items-start justify-between gap-3'>
                                    <h2
                                        id='profile-pw-title'
                                        className='flex items-center gap-2 text-lg font-bold text-slate-900'
                                    >
                                        <FiLock className='text-indigo-600' />
                                        Şifre değiştir
                                    </h2>
                                    <button
                                        type='button'
                                        onClick={() =>
                                            !submitting && closeModal()
                                        }
                                        disabled={submitting}
                                        className='rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 disabled:opacity-50'
                                        aria-label='Kapat'
                                    >
                                        <FiX size={20} />
                                    </button>
                                </div>
                                <p className='mt-2 text-sm text-slate-600'>
                                    Ana ekranda girdiğiniz mevcut şifre
                                    doğrulanır; yalnızca yeni şifreyi burada
                                    onaylayın.
                                </p>
                            </div>
                            <div className='space-y-4 px-6 py-6'>
                                <div>
                                    <label className='mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500'>
                                        Yeni şifre
                                    </label>
                                    <input
                                        type='password'
                                        value={newPassword}
                                        onChange={(e) =>
                                            setNewPassword(e.target.value)
                                        }
                                        disabled={submitting}
                                        className={inputClass}
                                        placeholder='Yeni şifre'
                                        autoComplete='new-password'
                                    />
                                </div>
                                <div>
                                    <label className='mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500'>
                                        Yeni şifre tekrar
                                    </label>
                                    <input
                                        type='password'
                                        value={newPasswordAgain}
                                        onChange={(e) =>
                                            setNewPasswordAgain(e.target.value)
                                        }
                                        disabled={submitting}
                                        className={inputClass}
                                        placeholder='Yeni şifre tekrar'
                                        autoComplete='new-password'
                                    />
                                </div>
                            </div>
                            <div className='flex justify-end gap-3 border-t border-slate-100 bg-slate-50/80 px-6 py-4'>
                                <button
                                    type='button'
                                    onClick={closeModal}
                                    disabled={submitting}
                                    className='rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50'
                                >
                                    İptal
                                </button>
                                <button
                                    type='button'
                                    onClick={submitPasswordChange}
                                    disabled={submitting}
                                    className='inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-indigo-500/25 transition hover:bg-indigo-700 disabled:pointer-events-none disabled:opacity-60'
                                >
                                    {submitting ? (
                                        <span className='h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent' />
                                    ) : (
                                        <>
                                            <FiSave size={18} />
                                            Kaydet
                                        </>
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className='mx-auto w-full max-w-[720px] space-y-6 px-4 pb-16 pt-2 sm:px-6 lg:px-8'>
                <div className='rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm sm:p-8'>
                    <div className='flex items-start gap-4'>
                        <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-md shadow-indigo-500/25'>
                            <FiUser className='text-xl' />
                        </div>
                        <div>
                            <h1 className='text-2xl font-bold tracking-tight text-slate-900'>
                                Profil
                            </h1>
                            <p className='mt-1 text-sm text-slate-600'>
                                Şifrenizi güncelleyin. İsim bilgisi sunucudan
                                gelir; değişiklik için yönetici gerekir.
                            </p>
                        </div>
                    </div>
                </div>

                <div className='rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm sm:p-8'>
                    {loadingProfile ? (
                        <div className='flex flex-col items-center justify-center gap-3 py-12'>
                            <div className='h-10 w-10 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent' />
                            <p className='text-sm text-slate-600'>
                                Profil yükleniyor…
                            </p>
                        </div>
                    ) : (
                        <div className='space-y-6'>
                            <div>
                                <label className='mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500'>
                                    Kullanıcı adı
                                </label>
                                <input
                                    type='text'
                                    value={name}
                                    readOnly
                                    className={`${inputClass} cursor-not-allowed bg-slate-100/80 text-slate-600`}
                                    title='Salt okunur'
                                />
                            </div>

                            <div>
                                <label className='mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500'>
                                    Mevcut şifre
                                </label>
                                <div className='relative'>
                                    <input
                                        type={
                                            showCurrentPassword
                                                ? 'text'
                                                : 'password'
                                        }
                                        value={currentPassword}
                                        onChange={(e) =>
                                            setCurrentPassword(e.target.value)
                                        }
                                        className={`${inputClass} pr-12`}
                                        placeholder='Şifre değiştirmek için girin'
                                        autoComplete='current-password'
                                    />
                                    <button
                                        type='button'
                                        onClick={() =>
                                            setShowCurrentPassword(
                                                (v) => !v
                                            )
                                        }
                                        className='absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-slate-600'
                                        aria-label={
                                            showCurrentPassword
                                                ? 'Şifreyi gizle'
                                                : 'Şifreyi göster'
                                        }
                                    >
                                        {showCurrentPassword ? (
                                            <FaEyeSlash size={20} />
                                        ) : (
                                            <FaEye size={20} />
                                        )}
                                    </button>
                                </div>
                            </div>

                            <div className='flex justify-end pt-2'>
                                <button
                                    type='button'
                                    onClick={() => setOpen(true)}
                                    className='inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition hover:bg-indigo-700'
                                >
                                    <FiLock size={18} />
                                    Şifre değiştir
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Container>
    )
}

export default Profile
